'use client'

import { CatalogQuickCreate } from '@/components/catalog/catalog-quick-create'
import PaginationControls from '@/components/pagination/PaginationControls'
import {
    addCatalogVariation,
    Catalog,
    CatalogVariation,
    getCatalogsByCompany,
    getCatalogVariations,
} from '@/lib/api/catalog/catalog-api'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useAuth } from '@/store/hooks'
import { useCallback, useMemo, useState } from 'react'
import {
    MdAdd,
    MdBiotech,
    MdCheck,
    MdClose,
    MdExpandLess,
    MdExpandMore,
    MdSearch,
} from 'react-icons/md'
import { toast } from 'react-toastify'

/** Item escolhido, com as variações marcadas nele. */
export interface SelectedExam {
    catalog: Catalog
    /** Variações-como-item (Catalog filhos) escolhidas. Vazio = item sem variação. */
    variations: { id: number; name: string }[]
}

interface Props {
    /** Escolha atual. Controlada de fora: quem usa decide o que fazer com ela. */
    selected: SelectedExam[]
    onSelectedChange: (next: SelectedExam[]) => void
    /** Faixa acima da busca — o pedido usa para as sugestões da IA. */
    banner?: React.ReactNode
    /** Itens criados no cadastro rápido, fixados no topo da lista. */
    createdItems?: Catalog[]
    onItemCreated?: (catalog: Catalog) => void
}

/**
 * Seleção de exames do catálogo, com busca paginada, variações e cadastro rápido.
 *
 * Extraído do `ExamRequestModal`, que era o único lugar onde essa lógica existia, para
 * ser reaproveitado ao montar um MODELO de pedido (`Template` de categoria
 * `EXAM_REQUEST`). Duplicar a tela faria as duas divergirem na primeira mudança — e a
 * mais provável era justamente a que interessa aqui: cadastrar o exame que falta sem
 * abandonar o que já foi marcado.
 *
 * O catálogo é o mesmo (`Catalog` categoria EXAM, com as variações-como-item) e a
 * busca é `isListing`: sem isso ela esconde o que não tem parceiro atrelado, e pedido
 * médico não tem parceiro nenhum.
 */
export default function ExamPicker({
    selected,
    onSelectedChange,
    banner,
    createdItems = [],
    onItemCreated,
}: Props) {
    const { user } = useAuth()
    const companyId = user?.company?.id

    const [searchTerm, setSearchTerm] = useState('')
    const debouncedSearch = useDebounce(searchTerm, 300)
    const [showQuickCreate, setShowQuickCreate] = useState(false)

    const [expandedId, setExpandedId] = useState<number | null>(null)
    const [variationsByCatalog, setVariationsByCatalog] = useState<
        Record<number, CatalogVariation[]>
    >({})
    const [loadingVariations, setLoadingVariations] = useState<number | null>(
        null
    )
    const [newVariationName, setNewVariationName] = useState('')
    const [creatingVariation, setCreatingVariation] = useState(false)

    const fetchFn = useCallback(
        (params: any) => getCatalogsByCompany(companyId as number, params),
        [companyId]
    )

    const filters = useMemo(
        () => ({
            category: 'EXAM' as const,
            activeOnly: true,
            search: debouncedSearch.trim() || undefined,
            // Listagem, não agendamento: sem isto a busca esconde o que não tem
            // parceiro atrelado — e pedido médico não tem parceiro nenhum.
            isListing: true,
        }),
        [debouncedSearch]
    )

    const { items, loading, page, limit, total, setPage } = usePaginatedPage<
        Catalog,
        typeof filters
    >({
        fetchFn,
        filters,
        limit: 10,
        enabled: !!companyId,
    })

    const listItems = useMemo(
        () => [
            ...createdItems,
            ...items.filter(
                item => !createdItems.some(created => created.id === item.id)
            ),
        ],
        [createdItems, items]
    )

    /** Mantém a assinatura de `setSelected` que o código extraído já usava. */
    const setSelected = (
        updater: SelectedExam[] | ((previous: SelectedExam[]) => SelectedExam[])
    ) => {
        onSelectedChange(
            typeof updater === 'function' ? updater(selected) : updater
        )
    }

    const findSelected = (catalogId: number) =>
        selected.find(item => item.catalog.id === catalogId)

    const toggleItem = (catalog: Catalog) => {
        setSelected(previous =>
            previous.some(item => item.catalog.id === catalog.id)
                ? previous.filter(item => item.catalog.id !== catalog.id)
                : [...previous, { catalog, variations: [] }]
        )
    }

    const removeItem = (catalogId: number) => {
        setSelected(previous =>
            previous.filter(item => item.catalog.id !== catalogId)
        )
    }

    /**
     * Marcar uma variação SELECIONA o item: o médico que abre "Ressonância" e marca
     * "com contraste" está pedindo a ressonância — obrigá-lo a marcar as duas coisas
     * é uma pegadinha, e esquecer a de cima deixaria a variação fora do pedido.
     *
     * Desmarcar a última variação não tira o item: continua sendo um pedido válido,
     * agora sem variação.
     */
    const toggleVariation = (catalog: Catalog, variation: CatalogVariation) => {
        setSelected(previous => {
            const current = previous.find(
                item => item.catalog.id === catalog.id
            )
            if (!current) {
                return [
                    ...previous,
                    {
                        catalog,
                        variations: [
                            { id: variation.id, name: variation.name },
                        ],
                    },
                ]
            }
            const has = current.variations.some(v => v.id === variation.id)
            return previous.map(item =>
                item.catalog.id === catalog.id
                    ? {
                          ...item,
                          variations: has
                              ? item.variations.filter(
                                    v => v.id !== variation.id
                                )
                              : [
                                    ...item.variations,
                                    { id: variation.id, name: variation.name },
                                ],
                      }
                    : item
            )
        })
    }

    const toggleExpand = async (catalog: Catalog) => {
        setNewVariationName('')
        if (expandedId === catalog.id) {
            setExpandedId(null)
            return
        }
        setExpandedId(catalog.id)
        if (variationsByCatalog[catalog.id]) return

        setLoadingVariations(catalog.id)
        try {
            const response = await getCatalogVariations(catalog.id)
            setVariationsByCatalog(previous => ({
                ...previous,
                [catalog.id]: response.success ? response.result || [] : [],
            }))
        } catch (error) {
            setVariationsByCatalog(previous => ({
                ...previous,
                [catalog.id]: [],
            }))
        } finally {
            setLoadingVariations(null)
        }
    }

    /** Variação nova nasce sem preço, como o item do pedido. */
    const handleCreateVariation = async (catalog: Catalog) => {
        const name = newVariationName.trim()
        if (!name || !companyId) return

        setCreatingVariation(true)
        try {
            const response = await addCatalogVariation(catalog.id, {
                name,
                companyId,
                active: true,
            })
            if (!response.success || !response.result) {
                toast.error(response.error || 'Falha ao criar variação')
                return
            }
            const created = response.result
            setVariationsByCatalog(previous => ({
                ...previous,
                [catalog.id]: [...(previous[catalog.id] || []), created],
            }))
            // Já entra marcada: quem cadastrou estava pedindo.
            toggleVariation(catalog, created)
            setNewVariationName('')
            toast.success('Variação criada')
        } catch (error) {
            toast.error('Falha ao criar variação')
        } finally {
            setCreatingVariation(false)
        }
    }

    return (
        <>
            <>
                {banner}

                {/* Busca + cadastro. O botão fica AQUI, e não no fim da
                                lista: no rodapé ele saía de vista com a rolagem, que é
                                justamente quando o médico conclui que o exame não
                                existe no catálogo. */}
                <div className="border-b border-base-300/60 px-6 py-3">
                    {/* `items-stretch`: o botão herda a altura do campo em
                                    vez de fixar a dele — altura chumbada erra sempre que
                                    o tema muda o tamanho do input. */}
                    <div className="flex items-stretch gap-2">
                        <label className="input input-bordered flex flex-1 items-center gap-2">
                            <MdSearch className="text-lg text-base-content/50" />
                            <input
                                type="text"
                                className="grow"
                                placeholder="Buscar exame no catálogo..."
                                value={searchTerm}
                                onChange={event =>
                                    setSearchTerm(event.target.value)
                                }
                            />
                        </label>
                        <button
                            type="button"
                            className="btn btn-outline h-auto min-h-0 gap-1 flex-shrink-0"
                            onClick={() => setShowQuickCreate(true)}
                            title="Cadastrar um exame novo no catálogo"
                        >
                            <MdAdd className="text-base" />
                            <span className="hidden sm:inline">
                                Cadastrar exame
                            </span>
                        </button>
                    </div>
                </div>

                {/* Lista do catálogo */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center min-h-[200px] gap-3">
                            <div className="loading loading-spinner loading-lg text-secondary"></div>
                            <p className="text-sm text-base-content/60">
                                Carregando exames...
                            </p>
                        </div>
                    ) : listItems.length === 0 ? (
                        <div className="text-center py-10">
                            <MdBiotech className="text-4xl mx-auto mb-3 text-base-content/20" />
                            <p className="text-base-content/60">
                                {debouncedSearch.trim()
                                    ? `Nenhum exame encontrado para "${debouncedSearch.trim()}"`
                                    : 'Nenhum exame no catálogo'}
                            </p>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm gap-1 mt-4"
                                onClick={() => setShowQuickCreate(true)}
                            >
                                <MdAdd className="text-base" />
                                Cadastrar no catálogo
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {listItems.map(item => {
                                const chosen = findSelected(item.id)
                                const checked = !!chosen
                                const expanded = expandedId === item.id
                                const variations = variationsByCatalog[item.id]
                                return (
                                    <div
                                        key={item.id}
                                        className={`rounded-xl border transition-colors ${
                                            checked
                                                ? 'border-secondary bg-secondary/10'
                                                : 'border-base-300/50'
                                        }`}
                                    >
                                        {/* A LINHA inteira seleciona, não só
                                                        o texto. Enquanto o alvo era um
                                                        `button` em volta do nome, o
                                                        padding do bloco e o espaço à
                                                        direita não clicavam — e um card
                                                        que reage só no nome parece
                                                        quebrado. */}
                                        <div
                                            className="flex cursor-pointer items-center gap-1 p-3"
                                            role="button"
                                            tabIndex={0}
                                            aria-pressed={checked}
                                            onClick={() => toggleItem(item)}
                                            onKeyDown={event => {
                                                if (
                                                    event.key === 'Enter' ||
                                                    event.key === ' '
                                                ) {
                                                    event.preventDefault()
                                                    toggleItem(item)
                                                }
                                            }}
                                        >
                                            <span className="flex min-w-0 flex-1 items-center gap-3 text-left">
                                                <span
                                                    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border ${
                                                        checked
                                                            ? 'border-secondary bg-secondary text-secondary-content'
                                                            : 'border-base-300'
                                                    }`}
                                                >
                                                    {checked && (
                                                        <MdCheck className="text-sm" />
                                                    )}
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="flex items-center gap-2 font-medium text-base-content">
                                                        <span className="truncate">
                                                            {item.name}
                                                        </span>
                                                        {createdItems.some(
                                                            created =>
                                                                created.id ===
                                                                item.id
                                                        ) && (
                                                            <span className="badge badge-secondary badge-sm flex-shrink-0">
                                                                Novo
                                                            </span>
                                                        )}
                                                    </span>
                                                    {chosen &&
                                                    chosen.variations.length >
                                                        0 ? (
                                                        <span className="block text-xs text-secondary">
                                                            {chosen.variations
                                                                .map(
                                                                    v => v.name
                                                                )
                                                                .join(', ')}
                                                        </span>
                                                    ) : (
                                                        item.tussCode && (
                                                            <span className="block text-xs text-base-content/50">
                                                                TUSS{' '}
                                                                {item.tussCode}
                                                            </span>
                                                        )
                                                    )}
                                                </span>
                                            </span>
                                            {/* Variações do item: só abre
                                                            quem precisa delas. */}
                                            <button
                                                type="button"
                                                onClick={event => {
                                                    // Sem isto, abrir as
                                                    // variações marcaria o
                                                    // item junto.
                                                    event.stopPropagation()
                                                    toggleExpand(item)
                                                }}
                                                className="btn btn-ghost btn-sm btn-square"
                                                title="Variações deste exame"
                                                aria-expanded={expanded}
                                            >
                                                {expanded ? (
                                                    <MdExpandLess className="text-lg" />
                                                ) : (
                                                    <MdExpandMore className="text-lg" />
                                                )}
                                            </button>
                                        </div>

                                        {expanded && (
                                            <div className="border-t border-base-300/50 px-3 py-3">
                                                {loadingVariations ===
                                                item.id ? (
                                                    <div className="flex items-center gap-2 text-sm text-base-content/60">
                                                        <span className="loading loading-spinner loading-xs" />
                                                        Carregando variações...
                                                    </div>
                                                ) : (
                                                    <>
                                                        {variations &&
                                                        variations.length >
                                                            0 ? (
                                                            <div className="flex flex-wrap gap-2">
                                                                {variations.map(
                                                                    variation => {
                                                                        const variationChecked =
                                                                            !!chosen?.variations.some(
                                                                                v =>
                                                                                    v.id ===
                                                                                    variation.id
                                                                            )
                                                                        return (
                                                                            <button
                                                                                key={
                                                                                    variation.id
                                                                                }
                                                                                type="button"
                                                                                onClick={() =>
                                                                                    toggleVariation(
                                                                                        item,
                                                                                        variation
                                                                                    )
                                                                                }
                                                                                className={`btn btn-xs gap-1 ${
                                                                                    variationChecked
                                                                                        ? 'btn-secondary'
                                                                                        : 'btn-outline'
                                                                                }`}
                                                                            >
                                                                                {variationChecked && (
                                                                                    <MdCheck className="text-sm" />
                                                                                )}
                                                                                {
                                                                                    variation.name
                                                                                }
                                                                            </button>
                                                                        )
                                                                    }
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-base-content/60">
                                                                Este exame ainda
                                                                não tem
                                                                variações.
                                                            </p>
                                                        )}

                                                        {/* Cadastrar a variação que falta sem sair do pedido. */}
                                                        <div className="mt-3 flex items-center gap-2">
                                                            <input
                                                                type="text"
                                                                className="input input-bordered input-sm flex-1"
                                                                placeholder="Nova variação (ex.: com contraste)"
                                                                value={
                                                                    newVariationName
                                                                }
                                                                onChange={event =>
                                                                    setNewVariationName(
                                                                        event
                                                                            .target
                                                                            .value
                                                                    )
                                                                }
                                                                onKeyDown={event => {
                                                                    if (
                                                                        event.key ===
                                                                        'Enter'
                                                                    ) {
                                                                        event.preventDefault()
                                                                        handleCreateVariation(
                                                                            item
                                                                        )
                                                                    }
                                                                }}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline btn-sm gap-1"
                                                                onClick={() =>
                                                                    handleCreateVariation(
                                                                        item
                                                                    )
                                                                }
                                                                disabled={
                                                                    creatingVariation ||
                                                                    !newVariationName.trim()
                                                                }
                                                            >
                                                                {creatingVariation ? (
                                                                    <span className="loading loading-spinner loading-xs" />
                                                                ) : (
                                                                    <MdAdd className="text-base" />
                                                                )}
                                                                Adicionar
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {items.length > 0 && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="exame"
                            className="mt-4"
                        />
                    )}
                </div>
            </>
        </>
    )
}
